/**
 * Publishing an approved revision to the public curriculum API, and taking it
 * back down.
 *
 * @module components/portfolio/PublicationPanel
 */

import React, { useState } from "react";

import type { UsePublicationResult } from "@/components/portfolio/usePublication";
import { isPublished } from "@/components/portfolio/usePublication";
import { WithdrawPublicationDialog } from "@/components/portfolio/WithdrawPublicationDialog";
import {
  Alert,
  Button,
  FormField,
  Heading,
  KeyValue,
  Modal,
  MutedText,
  TextField,
  Time,
} from "@/components/ui";
import { ListBones } from "@/components/ui/SkeletonParts";

export interface PublicationPanelProps {
  publication: UsePublicationResult;
  /** Whether the tenant's entitlement allows publishing at all. */
  canPublish: boolean;
  /** Change management for an account that may open a revalidation. */
  changeManagement?: React.ReactNode;
}

/**
 * @example
 * ```tsx
 * {publication.panel && <PublicationPanel publication={publication} canPublish />}
 * ```
 */
export function PublicationPanel({
  publication,
  canPublish,
  changeManagement = undefined,
}: PublicationPanelProps): React.ReactElement | null {
  const { panel, busy, error } = publication;
  const [withdrawing, setWithdrawing] = useState(false);
  const closeWithdrawal = (): void => {
    setWithdrawing(false);
    publication.setWithdrawalReason("");
  };
  const withdraw = (): void => {
    publication
      .withdraw()
      .then((withdrawn) => {
        if (withdrawn) {
          setWithdrawing(false);
        }
      })
      .catch(() => undefined);
  };
  if (!panel) {
    return null;
  }

  const { revision, preview, mode } = panel;
  const latest = revision.latestPublication;
  const publicPath = latest
    ? `/api/public/v1/providers/${encodeURIComponent(latest.providerSlug)}/programmes/${encodeURIComponent(latest.publicProgrammeId)}`
    : undefined;

  return (
    // A dialog, not a band above the list: the panel pushed the row it acts
    // on down the page, and its own close button sat orphaned once the
    // heading scrolled away. `Modal` carries the title, the subtitle and the
    // dismissal, so none of that is hand-rolled here.
    <Modal
      open
      onClose={publication.close}
      title="Manage approved programme"
      subtitle={`${revision.institutionalLabel}, revision ${revision.revisionNumber}`}
      size="lg"
      data-testid="portfolio-publication-panel"
    >
      <Heading level={3} size="md">
        {revision.title}
      </Heading>

      {error && !withdrawing ? (
        <div className="mt-4">
          <Alert tone="warning" data-testid="portfolio-publication-error">
            <span className="flex flex-wrap items-center gap-3">
              {error}
              {/* A preview that never arrived leaves nothing to act on here. */}
              {mode === "preview" && !preview && !busy ? (
                <Button
                  size="sm"
                  variant="outline"
                  icon="arrow-clockwise"
                  onClick={() => {
                    publication.open(revision);
                  }}
                >
                  Try again
                </Button>
              ) : null}
            </span>
          </Alert>
        </div>
      ) : null}

      {!canPublish && (
        <div className="mt-4">
          <Alert tone="info" data-testid="portfolio-publication-entitlement-required">
            {isPublished(revision)
              ? "Publishing to the public curriculum API is no longer enabled for this tenant. This revision stays published until it is withdrawn."
              : "Publishing to the public curriculum API is not enabled for this tenant."}
          </Alert>
        </div>
      )}

      {canPublish && mode === "preview" && !preview && busy && (
        <div className="mt-5">
          <div data-testid="portfolio-publication-loading">
            <ListBones rows={3} label="Preparing the publication preview…" />
          </div>
        </div>
      )}

      {canPublish && mode === "preview" && preview && (
        <div className="mt-5 flex flex-col gap-5" data-testid="portfolio-publication-preview">
          {preview.issues.length > 0 && (
            <Alert
              tone="warning"
              title={
                preview.issues.some((issue) => issue.severity === "blocking")
                  ? "Publication blockers"
                  : "Before you publish"
              }
            >
              <ul className="mt-1 list-disc space-y-1 pl-5">
                {preview.issues.map((issue) => (
                  <li key={`${issue.code}:${issue.sectionPath ?? issue.message}`}>
                    {issue.message}
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {preview.payload && (
            <KeyValue
              entries={[
                {
                  label: "Award",
                  value: `${preview.payload.award.awardType}, NFQ ${preview.payload.award.nfqLevel}`,
                },
                {
                  label: "Credit volume",
                  value: `${preview.payload.award.credits} ${preview.payload.award.creditUnit}`,
                },
                { label: "Public scopes", value: preview.payload.applicability.length },
                { label: "Modules", value: preview.payload.modules.length },
              ]}
            />
          )}

          <MutedText size="caption">
            Payload hash <span className="font-mono break-all">{preview.payloadHash}</span>
          </MutedText>

          <FormField
            label="Publication note"
            htmlFor="portfolio-publication-note"
            helpText="Optional. Saved with this edition."
          >
            <TextField
              id="portfolio-publication-note"
              value={publication.note}
              onChange={publication.setNote}
              placeholder="e.g. First publication of the 2026 revision"
              data-testid="portfolio-publication-note"
            />
          </FormField>

          <div className="flex justify-end">
            <Button
              variant="primary"
              icon="upload-simple"
              loading={busy}
              loadingLabel="Publishing"
              disabled={!preview.readyToPublish}
              disabledReason="Resolve the publication blockers above first"
              onClick={publication.publish}
              data-testid="portfolio-publication-publish"
            >
              Publish this revision
            </Button>
          </div>
        </div>
      )}

      {mode === "published" && latest && (
        <div className="mt-5 flex flex-col gap-5" data-testid="portfolio-publication-manage">
          {latest.withdrawnAt ? (
            <Alert tone="warning">
              Withdrawn on <Time value={latest.withdrawnAt} variant="date" />.
              {latest.withdrawalReason ? ` ${latest.withdrawalReason}` : ""}
            </Alert>
          ) : (
            <Alert tone="success">
              Edition {latest.publicationSequence} was published on{" "}
              <Time value={latest.publishedAt} variant="date" />.
            </Alert>
          )}

          {publicPath && !latest.withdrawnAt && (
            <MutedText size="caption" data-testid="portfolio-publication-public-link">
              Public address <span className="font-mono break-all">{publicPath}</span>
            </MutedText>
          )}

          {latest.withdrawnAt
            ? canPublish && (
                <div className="flex justify-end">
                  <Button
                    icon="arrow-counter-clockwise"
                    disabled={busy}
                    disabledReason="Wait for the publication change to finish"
                    onClick={() => {
                      publication.open(revision);
                    }}
                    data-testid="portfolio-publication-preview-again"
                  >
                    Preview a new edition
                  </Button>
                </div>
              )
            : isPublished(revision) && (
                // Its own decision, behind its own dialog: opening a change
                // below leaves the publication as it is.
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    tone="danger"
                    icon="cloud-slash"
                    disabled={busy}
                    disabledReason="Wait for the publication change to finish"
                    onClick={() => setWithdrawing(true)}
                    data-testid="portfolio-publication-withdraw"
                  >
                    Withdraw publication…
                  </Button>
                </div>
              )}
        </div>
      )}

      {changeManagement}

      <WithdrawPublicationDialog
        open={withdrawing}
        edition={latest?.publicationSequence ?? 0}
        reason={publication.withdrawalReason}
        onReasonChange={publication.setWithdrawalReason}
        busy={busy}
        error={error}
        onWithdraw={withdraw}
        onClose={closeWithdrawal}
      />
    </Modal>
  );
}
